/**
 * Pagination Constants
 *
 * Centralized pagination configuration used across all table components.
 * Defines default page sizes and available page size options for consistent UX.
 */

/**
 * Default page size for table pagination
 * Used as the initial value when a table component mounts
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Available page size options for table pagination dropdowns
 * Users can select from these options to change how many rows are displayed per page
 */
export const PAGE_SIZE_OPTIONS: number[] = [20, 50, 100, 1000];

/**
 * Type for page size options - ensures type safety when using page size values
 */
export type PageSizeOption = 20 | 50 | 100 | 1000;

/**
 * Default current page (first page)
 * Used as the initial value for current page state
 */
export const DEFAULT_CURRENT_PAGE = 1;

/**
 * Pagination configuration object
 * Can be spread directly into table components for consistent setup
 *
 * @example
 * ```tsx
 * const [currentPage, setCurrentPage] = useState(PAGINATION_DEFAULTS.currentPage);
 * const [pageSize, setPageSize] = useState(PAGINATION_DEFAULTS.pageSize);
 *
 * <Table
 *   pagination={{
 *     currentPage,
 *     pageSize,
 *     pageSizeOptions: PAGINATION_DEFAULTS.pageSizeOptions,
 *     ...
 *   }}
 * />
 * ```
 */
export const PAGINATION_DEFAULTS = {
  currentPage: DEFAULT_CURRENT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  pageSizeOptions: PAGE_SIZE_OPTIONS,
};
